import {
  ACTION_TYPES,
  OPS,
  type ActionSpec,
  type AutomationRule,
  type Trigger,
} from "./types.ts";
import { isKnownField } from "./fields.ts";
import { isValidBusinessDays } from "./delays.ts";

export type Issue = { path: string; code: string };
const ONE_TO_ONE: readonly Trigger[] = [
  "call_outcome",
  "deal_created",
  "deal_stage_changed",
  "meeting_note_matched",
  "email_event",
];

const REQUIRED: Record<string, readonly string[]> = {
  set_callback: [],
  set_prospect_status: ["status"],
  cool_down: ["days"],
  create_task: ["text", "due"],
  create_deal: ["stage", "name_template"],
  move_deal: ["stage"],
  add_note: ["text_template"],
  enroll_sequence: ["sequence_id"],
  stop_sequences: ["reason"],
  send_email: ["template", "via", "to"],
  create_calendar_event: ["from"],
  notify_teams: ["template"],
  notify_user: ["who", "message_template"],
  suppress: ["channels"],
  ai_draft: ["trigger"], // GAVION ai-email
};

const NESTED_QUANTIFIER = /(\([^)]*[+*][^)]*\)[+*{])/;

function checkRegex(value: unknown, errors: Issue[]): void {
  const path = "conditions.value";
  if (typeof value !== "string" || value.length > 200) {
    errors.push({ path, code: "invalid_regex" });
    return;
  }
  try {
    new RegExp(value);
  } catch {
    errors.push({ path, code: "invalid_regex" });
    return;
  }
  if (NESTED_QUANTIFIER.test(value))
    errors.push({ path, code: "unsafe_regex" });
}

function walk(
  c: unknown,
  depth: number,
  trigger: Trigger,
  errors: Issue[],
  warnings: Issue[],
): void {
  if (!c || typeof c !== "object") return;
  const node = c as Record<string, unknown>;
  const children = (node.all ?? node.any) as unknown;
  if (Array.isArray(children)) {
    if (depth >= 2) {
      errors.push({ path: "conditions", code: "too_deep" });
      return;
    }
    children.forEach((x) => walk(x, depth + 1, trigger, errors, warnings));
    return;
  }
  if (typeof node.field === "string") {
    if (!OPS.includes(node.op as never))
      errors.push({ path: "conditions.op", code: "unknown_op" });
    if (node.op === "matches") checkRegex(node.value, errors);
    if (!isKnownField(trigger, node.field))
      warnings.push({ path: "conditions.field", code: "unknown_field" });
  }
}

export function validateRule(
  rule: AutomationRule,
  opts: { knownTemplates: readonly string[] | null },
): { errors: Issue[]; warnings: Issue[] } {
  const errors: Issue[] = [];
  const warnings: Issue[] = [];
  // GAVION (B2-T6): malformed input returns errors instead of throwing.
  if (!rule || typeof rule !== "object" || Array.isArray(rule))
    return { errors: [{ path: "", code: "invalid_rule" }], warnings };
  if (typeof rule.name !== "string" || !rule.name.trim())
    errors.push({ path: "name", code: "required" });
  // GAVION (B2-T6): null/missing conditions never fire; `{}` is the explicit "always".
  if (rule.conditions === null || rule.conditions === undefined)
    errors.push({ path: "conditions", code: "conditions_required" });
  walk(rule.conditions, 0, rule.trigger, errors, warnings);
  if (!Array.isArray(rule.actions)) {
    errors.push({ path: "actions", code: "actions_not_array" });
    return { errors, warnings };
  }
  rule.actions.forEach((a: ActionSpec, i) => {
    if (!a || typeof a !== "object") {
      errors.push({ path: `actions.${i}`, code: "unknown_action" });
      return;
    }
    if (!ACTION_TYPES.includes(a.type)) {
      errors.push({ path: `actions.${i}.type`, code: "unknown_action" });
      return;
    }
    for (const p of REQUIRED[a.type]) {
      if (a[p] === undefined || a[p] === null || a[p] === "")
        errors.push({ path: `actions.${i}.${p}`, code: "required" });
    }
    // GAVION (B2-T6): business_days must be an integer in 0..366 wherever a delay can carry it.
    if (
      [a, a.at, a.due, a.delay].some(
        (d) =>
          !!d &&
          typeof d === "object" &&
          "business_days" in d &&
          !isValidBusinessDays((d as { business_days: unknown }).business_days),
      )
    )
      errors.push({
        path: `actions.${i}.business_days`,
        code: "invalid_business_days",
      });
    if (
      a.type === "set_callback" &&
      !(a.delay && typeof a.delay === "object") &&
      a.hours === undefined &&
      a.before === undefined &&
      a.at === undefined &&
      a.from === undefined &&
      a.business_days === undefined &&
      a.minutes === undefined
    ) {
      errors.push({ path: `actions.${i}.at`, code: "required" });
    }
    if (
      a.type === "send_email" &&
      typeof a.template === "string" &&
      opts.knownTemplates &&
      !opts.knownTemplates.includes(a.template)
    ) {
      errors.push({ path: `actions.${i}.template`, code: "unknown_template" });
    }
    if (
      a.type === "send_email" &&
      a.via === "ses" &&
      ONE_TO_ONE.includes(rule.trigger)
    ) {
      warnings.push({ path: `actions.${i}.via`, code: "prefer_graph" });
    }
  });
  return { errors, warnings };
}
